import { motion } from "framer-motion";
import { Ear, ShieldCheck, Lock, HeartHandshake, Clock, Wallet, Quote, MessageCircle, PhoneCall, Video, Send, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import CrisisBanner from "@/components/site/CrisisBanner";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import BookingForm from "@/components/site/BookingForm";
import { WHATSAPP_LINK } from "@/config";

const HERO_IMG = "https://images.pexels.com/photos/1128796/pexels-photo-1128796.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";
const NARRATIVE_IMG = "https://images.pexels.com/photos/7622876/pexels-photo-7622876.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut", delay: i * 0.1 } }),
};

const WHY = [
  { icon: Ear, title: "A Safe Space to Vent", text: "Let it all out without holding back. No filter, no fear — just release." },
  { icon: ShieldCheck, title: "Zero Judgment Zone", text: "You won't be analysed or corrected. You'll simply be accepted as you are." },
  { icon: Lock, title: "Complete Confidentiality", text: "What you share stays between us. Your privacy is deeply respected." },
  { icon: HeartHandshake, title: "Emotional Relief", text: "Being truly heard is lighter than carrying it alone. Feel the weight lift." },
  { icon: Clock, title: "Flexible & Convenient", text: "Phone, video, or text — on days and times that work for your life." },
  { icon: Wallet, title: "Affordable Support", text: "Gentle, honest pricing so being heard never feels out of reach." },
];

const STEPS = [
  { icon: Send, title: "Request", text: "Fill the short request form or tap the WhatsApp button. No payment needed." },
  { icon: MessageCircle, title: "Chat & Confirm on WhatsApp", text: "We connect privately, pick a time, and share payment details in chat." },
  { icon: Video, title: "Join Your Session", text: "Meet on your chosen mode — a phone call, video link, or text chat." },
  { icon: HeartHandshake, title: "Share & Feel Better", text: "Speak freely and be truly heard. Leave feeling lighter." },
];

const TESTIMONIALS = [
  { name: "Priya", role: "Student, 21", text: "I didn't want advice — I just needed someone to listen. For the first time in months I felt understood without being judged." },
  { name: "Rahul", role: "Working professional", text: "I was carrying so much alone. One honest conversation here helped me breathe again. It felt safe and warm." },
  { name: "Anjali", role: "Homemaker, 38", text: "I'd been feeling unheard at home for a long time. Having someone listen with real warmth, without judging me, made a bigger difference than I ever expected." },
];

const FAQS = [
  { q: "Is this counselling or clinical treatment?", a: "This is a supportive listening and guidance service by an experienced, compassionate listener — not clinical or medical treatment. If you need clinical care, we'll gently encourage you to reach a licensed professional." },
  { q: "Who will I be talking to?", a: "An experienced, compassionate listener who genuinely wants to hear you. The listener stays anonymous so the focus remains entirely on you." },
  { q: "Is it really confidential?", a: "Yes. What you share stays private. The only limit is if there's a serious risk of harm to you or someone else — in which case we may encourage professional or emergency support." },
  { q: "How do I pay?", a: "There's no online checkout. Once we confirm your session on WhatsApp, we share simple payment details (like UPI) privately. You only pay after we've agreed on a time." },
  { q: "What if I'm under 18?", a: "You're welcome here. Please have a parent or guardian's consent before booking, and tick the consent box on the request form." },
  { q: "What if I'm in crisis right now?", a: "Please reach out to Tele-MANAS (14416), iCall, or AASRA immediately. This service is not for emergencies or crisis intervention." },
];

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`max-w-6xl mx-auto px-4 py-20 sm:py-28 ${className}`}>{children}</section>
);

const Landing = () => {
  return (
    <div className="bg-background text-foreground">
      <CrisisBanner />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Soft sunlit wildflower meadow on a gentle morning" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2D312E]/45 via-[#2D312E]/30 to-[#2D312E]/45" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 py-32 sm:py-44 text-center text-white">
          <motion.p initial="hidden" animate="show" variants={fade} className="mb-5 inline-block rounded-full bg-white/15 backdrop-blur-sm px-4 py-1.5 text-sm">
            You don't have to carry it alone
          </motion.p>
          <motion.h1 initial="hidden" animate="show" custom={1} variants={fade} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight">
            Sometimes, you just need someone to listen.
          </motion.h1>
          <motion.p initial="hidden" animate="show" custom={2} variants={fade} className="mt-6 text-lg text-white/90 max-w-2xl mx-auto">
            A safe, judgment-free space where your feelings are valid and you are truly heard.
          </motion.p>
          <motion.div initial="hidden" animate="show" custom={3} variants={fade} className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#booking">
              <Button data-testid="hero-request-button" className="rounded-full px-8 py-6 text-base">Request a session</Button>
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button data-testid="hero-whatsapp-button" variant="outline" className="rounded-full px-8 py-6 text-base bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white">
                <MessageCircle className="w-5 h-5 mr-2" /> Chat with us
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Why Us */}
      <Section id="why-us">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">Why talk to us</h2>
          <p className="mt-4 text-muted-foreground">Not to be fixed. Not to be judged. Just to be heard.</p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w, i) => (
            <motion.div key={w.title} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i} variants={fade}
              data-testid={`why-card-${i}`}
              className="rounded-3xl bg-card border border-border p-8 transition-transform duration-500 ease-out hover:-translate-y-1 hover:shadow-md">
              <span className="flex items-center justify-center w-12 h-12 rounded-2xl bg-primary/12 text-primary mb-5">
                <w.icon className="w-6 h-6" />
              </span>
              <h3 className="font-serif text-xl mb-2">{w.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{w.text}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Power of Being Heard */}
      <section className="bg-secondary/50">
        <div className="max-w-6xl mx-auto px-4 py-20 sm:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
            <img src={NARRATIVE_IMG} alt="A person journaling quietly" className="rounded-3xl w-full object-cover shadow-md" />
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} variants={fade}>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight mb-6">The power of being heard</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              When someone truly listens — without rushing to fix, advise, or judge — something inside us softens.
              The tightness in the chest eases. The thoughts that felt tangled begin to settle.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You don't need the perfect words. You don't need to have it all figured out. You just need a space
              where your feelings are allowed to exist, exactly as they are.
            </p>
            <p className="text-foreground font-medium leading-relaxed">
              That's what we're here for. A calm, patient presence — for as long as you need to be heard.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <Section id="how-it-works">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">How it works</h2>
          <p className="mt-4 text-muted-foreground">Four gentle steps. No pressure at any point.</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <motion.div key={s.title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
              data-testid={`step-card-${i}`} className="relative rounded-3xl bg-card border border-border p-7">
              <span className="absolute -top-3 -left-1 font-serif text-5xl text-primary/15 font-bold select-none">{i + 1}</span>
              <span className="relative flex items-center justify-center w-12 h-12 rounded-2xl bg-accent/15 text-accent mb-5">
                <s.icon className="w-6 h-6" />
              </span>
              <h3 className="font-serif text-lg mb-2">{s.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Pricing */}
      <section id="pricing" className="bg-secondary/50">
        <div className="max-w-5xl mx-auto px-4 py-20 sm:py-28">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">Gentle, honest pricing</h2>
            <p className="mt-4 text-muted-foreground">No online checkout. Payment is arranged privately over WhatsApp once we confirm.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div data-testid="pricing-single" className="rounded-3xl bg-card border border-border p-10">
              <p className="text-sm uppercase tracking-wide text-muted-foreground">Single session</p>
              <p className="font-serif text-5xl font-semibold my-3">₹499</p>
              <p className="text-muted-foreground mb-6">One full listening session in your chosen mode.</p>
              <ul className="space-y-3 text-sm mb-8">
                <li className="flex gap-2"><Star className="w-4 h-4 text-accent mt-0.5" /> Phone, video, or text chat</li>
                <li className="flex gap-2"><Star className="w-4 h-4 text-accent mt-0.5" /> Completely confidential</li>
                <li className="flex gap-2"><Star className="w-4 h-4 text-accent mt-0.5" /> No commitment beyond this session</li>
              </ul>
              <a href="#booking"><Button variant="outline" className="rounded-full w-full h-12">Request this</Button></a>
            </div>
            <div data-testid="pricing-pack" className="relative rounded-3xl bg-primary text-primary-foreground p-10 shadow-md">
              <span className="absolute top-6 right-6 rounded-full bg-white/20 px-3 py-1 text-xs">Best value</span>
              <p className="text-sm uppercase tracking-wide text-primary-foreground/80">3-session pack</p>
              <p className="font-serif text-5xl font-semibold my-3">₹999</p>
              <p className="text-primary-foreground/85 mb-6">Three sessions within one month, scheduled on mutual availability.</p>
              <ul className="space-y-3 text-sm mb-8">
                <li className="flex gap-2"><Star className="w-4 h-4 mt-0.5" /> Save vs. booking individually</li>
                <li className="flex gap-2"><Star className="w-4 h-4 mt-0.5" /> Continuity with the same listener</li>
                <li className="flex gap-2"><Star className="w-4 h-4 mt-0.5" /> Flexible scheduling within the month</li>
              </ul>
              <a href="#booking"><Button className="rounded-full w-full h-12 bg-white text-primary hover:bg-white/90">Request this</Button></a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Section id="testimonials">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">Kind words from people we've heard</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={t.name} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
              data-testid={`testimonial-${i}`} className="rounded-3xl bg-card border border-border p-8">
              <Quote className="w-8 h-8 text-accent/40 mb-4" />
              <p className="text-foreground/90 leading-relaxed mb-6">{t.text}</p>
              <p className="font-serif text-lg">{t.name}</p>
              <p className="text-sm text-muted-foreground">{t.role}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Booking */}
      <section id="booking" className="bg-secondary/50">
        <div className="max-w-3xl mx-auto px-4 py-20 sm:py-28">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">Request a session</h2>
            <p className="mt-4 text-muted-foreground">
              Tell us a little about what works for you. We'll reach out privately on WhatsApp to confirm — no payment is taken here.
            </p>
          </div>
          <BookingForm />
        </div>
      </section>

      {/* FAQ */}
      <Section id="faq" className="max-w-3xl">
        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">Questions, answered gently</h2>
        </div>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-border">
              <AccordionTrigger className="text-left font-serif text-lg hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-10 rounded-3xl bg-muted p-6 text-sm text-muted-foreground leading-relaxed" data-testid="disclaimer-box">
          <strong className="text-foreground">A gentle note:</strong> This is an emotional support, listening and guidance
          service by an experienced listener. It is NOT medical, psychological, psychiatric, or crisis treatment, and not a
          substitute for professional clinical care. If you are in danger or thinking of self-harm, please contact
          Tele-MANAS (14416), iCall, or AASRA immediately.
        </div>
      </Section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Landing;
